/**
 * Wanderlust Jewelry - renders the wishlist page from saved handles.
 * Uses the public /products/<handle>.js endpoint; products that were deleted or unpublished are dropped
 * from the list silently.
 */
// The shop's own money format (data-money-format = Liquid shop.money_format), so the list shows "R 3,450.00" like the
// rest of the site; Intl wrote "ZAR 3,450.00" (site audit 21 Oct W2). Same idea as formatMoney in wj-core.js.
const MONEY = document.querySelector('wj-wishlist-grid')?.getAttribute('data-money-format') || 'R {{amount}}';
const formatMoney = (cents) =>
  MONEY.replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class WjWishlistGrid extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.wj-wishlist-grid__list');
    this.status = this.querySelector('.wj-wishlist-grid__status');
    this.render();
    this.onChange = (event) => {
      if (!event.detail.added) {
        this.querySelector(`[data-handle="${CSS.escape(event.detail.handle)}"]`)?.closest('li')?.remove();
        if (!this.list.children.length) this.showEmpty();
      }
    };
    document.addEventListener('wj:wishlist-change', this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('wj:wishlist-change', this.onChange);
  }

  showEmpty() {
    this.status.replaceChildren(this.querySelector('template[data-empty]').content.cloneNode(true));
  }

  async render() {
    const handles = window.WJ?.wishlist?.all() ?? [];
    if (!handles.length) return this.showEmpty();

    const root = window.Shopify?.routes?.root || '/';
    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    const items = products.filter(Boolean);
    if (!items.length) return this.showEmpty();
    this.status.textContent = `${items.length} saved ${items.length === 1 ? 'piece' : 'pieces'}`;

    this.list.innerHTML = items
      .map((p) => {
        const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=600` : '';
        const price =
          p.price_varies ? `From ${formatMoney(p.price_min)}` : formatMoney(p.price);
        return `
        <li class="wj-wishlist-card">
          <a href="${escapeHtml(p.url)}">
            ${img ? `<img src="${escapeHtml(img)}" alt="${escapeHtml(p.title)}" loading="lazy" width="600" height="750">` : ''}
            <span class="wj-wishlist-card__title">${escapeHtml(p.title)}</span>
          </a>
          <span class="wj-wishlist-card__price">${price}${p.available ? '' : ' · Sold out'}</span>
          <wj-wishlist-button class="wj-wishlist wj-wishlist--icon" data-handle="${escapeHtml(p.handle)}">
            <button type="button" aria-pressed="true" aria-label="Remove ${escapeHtml(p.title)} from wishlist">
              <svg class="wj-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 20s-7.5-4.6-9-9.4C2 7 4.4 4 7.6 4c2 0 3.4 1.1 4.4 2.6C13 5.1 14.4 4 16.4 4 19.6 4 22 7 21 10.6 19.5 15.4 12 20 12 20z"/></svg>
            </button>
          </wj-wishlist-button>
        </li>`;
      })
      .join('');
  }
}

if (!customElements.get('wj-wishlist-grid')) customElements.define('wj-wishlist-grid', WjWishlistGrid);
